/*
 * `/api/time/*`. Routes answer 403 with `code: "time_tracking_off"` while the
 * firm's switch is off; callers treat that as "feature not available".
 */
import axiosInstance, { getAuthToken } from "../../../api/axiosConfig";
import type { Provider } from "../review/exportModel";
import { handleAPIError } from "../../../api/utils/errors";
import type {
  BeatSegment,
  BeatsResponse,
  BillingRule,
  CaseOption,
  CaseRules,
  CaseTimeSummary,
  EntriesResponse,
  ExpenseFilters,
  ExportBatch,
  ExportPreview,
  ExportRequest,
  FlagAction,
  FlagFix,
  FirmTimeSettings,
  FirmTimeSettingsPatch,
  GuidelineKind,
  GuidelineOwner,
  LearnedPreference,
  MatterRow,
  MyTimeSettings,
  MyTimeSettingsPatch,
  NewExpense,
  PrebillMatter,
  PrebillMatterRow,
  ReviewFilters,
  ReviewKpis,
  RuleDraft,
  RunningTimer,
  SignalRow,
  TimeBrief,
  TimeEntry,
  TimeExpense,
  TimeGuideline,
  Timekeeper,
  TimeMatter,
  TimeNudge,
  TimeStatus,
  TodaySummary,
  UnreleasedDay,
  WaitingRow
} from "../types";

/** The fields a person (or an admin on released time) may change. */
export type EntryPatch = Partial<
  Pick<
    TimeEntry,
    | "narrative"
    | "duration_hours"
    | "individual_id"
    | "utbms_activity"
    | "utbms_task"
    | "work_date"
    | "started_at"
    | "ended_at"
  >
> & { note?: string };

export type NewEntry = {
  work_date: string;
  hours: number;
  narrative?: string;
  individual_id?: string | null;
  started_at?: string;
  ended_at?: string;
  utbms_activity?: string;
};

export const rethrow = (error: unknown): never => {
  const apiError = handleAPIError(error);
  throw apiError;
};

const call = async <T>(run: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    return rethrow(error);
  }
};

const entries = (data: { entries: TimeEntry[] }) => data.entries;
const expenseList = (data: { expenses: TimeExpense[] }) => data.expenses;

export const TimeAPI = {
  status: () => call(() => axiosInstance.get<TimeStatus>("/time/status")),

  list: (from: string, to: string) =>
    call(() =>
      axiosInstance.get<EntriesResponse>("/time/entries", {
        params: { from, to }
      })
    ),
  get: (id: string) =>
    call(() =>
      axiosInstance.get<{ entry: TimeEntry }>(`/time/entries/${id}`)
    ).then((d) => d.entry),
  create: (body: NewEntry) =>
    call(() =>
      axiosInstance.post<{ entry: TimeEntry }>("/time/entries", body)
    ).then((d) => d.entry),
  update: (id: string, patch: EntryPatch) =>
    call(() =>
      axiosInstance.patch<{ entry: TimeEntry }>(`/time/entries/${id}`, patch)
    ).then((d) => d.entry),
  assign: (id: string, individualId: string) =>
    call(() =>
      axiosInstance.post<{
        entry: TimeEntry;
        learned: LearnedPreference | null;
      }>(`/time/entries/${id}/assign`, { individual_id: individualId })
    ),
  merge: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ entry: TimeEntry }>("/time/entries/merge", { ids })
    ).then((d) => d.entry),
  split: (id: string, hours: number) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>(
        `/time/entries/${id}/split`,
        { hours }
      )
    ).then(entries),
  unmerge: (id: string) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>(
        `/time/entries/${id}/unmerge`
      )
    ).then(entries),
  unsplit: (id: string) =>
    call(() =>
      axiosInstance.post<{ entry: TimeEntry }>(`/time/entries/${id}/unsplit`)
    ).then((d) => d.entry),
  discard: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/entries/discard", {
        ids
      })
    ).then(entries),
  restore: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/entries/restore", {
        ids
      })
    ).then(entries),
  release: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/entries/release", {
        ids
      })
    ).then(entries),
  recall: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/entries/recall", {
        ids
      })
    ).then(entries),

  startTimer: (individualId?: string | null, narrative?: string) =>
    call(() =>
      axiosInstance.post<{ timer: RunningTimer }>("/time/timer", {
        action: "start",
        individual_id: individualId ?? null,
        narrative: narrative ?? ""
      })
    ),
  stopTimer: (discard = false) =>
    call(() =>
      axiosInstance.post<{ timer: null; entry: TimeEntry | null }>(
        "/time/timer",
        { action: discard ? "discard" : "stop" }
      )
    ),

  today: () => call(() => axiosInstance.get<TodaySummary>("/time/me/today")),
  unreleased: () =>
    call(() =>
      axiosInstance.get<{ days: UnreleasedDay[] }>("/time/me/unreleased")
    ).then((d) => d.days),
  signals: (date: string) =>
    call(() =>
      axiosInstance.get<{ signals: SignalRow[]; date: string }>(
        "/time/me/signals",
        { params: { date } }
      )
    ),
  redraft: (date?: string) =>
    call(() =>
      axiosInstance.post<{ drafted: number }>("/time/me/draft", { date })
    ),
  brief: (date?: string) =>
    call(() =>
      axiosInstance.get<TimeBrief>("/time/brief", { params: { date } })
    ),

  mySettings: () =>
    call(() => axiosInstance.get<MyTimeSettings>("/time/me/settings")),
  saveMySettings: (patch: MyTimeSettingsPatch) =>
    call(() => axiosInstance.put<MyTimeSettings>("/time/me/settings", patch)),
  deletePreference: (id: string) =>
    call(() =>
      axiosInstance.delete<{ ok: boolean }>(`/time/me/preferences/${id}`)
    ),

  searchCases: (q: string) =>
    call(() =>
      axiosInstance.get<{ cases: CaseOption[] }>("/time/cases/search", {
        params: { q }
      })
    ).then((d) => d.cases),
  caseTime: (individualId: string) =>
    call(() =>
      axiosInstance.get<CaseTimeSummary>(`/time/cases/${individualId}`)
    ),
  caseBilling: (individualId: string) =>
    call(() =>
      axiosInstance.get<{ matter: TimeMatter }>(
        `/time/cases/${individualId}/billing`
      )
    ).then((d) => d.matter),
  saveCaseBilling: (individualId: string, patch: Partial<TimeMatter>) =>
    call(() =>
      axiosInstance.put<{ matter: TimeMatter }>(
        `/time/cases/${individualId}/billing`,
        patch
      )
    ).then((d) => d.matter)
};

export const ExpenseAPI = {
  forCase: (individualId: string) =>
    call(() =>
      axiosInstance.get<{ expenses: TimeExpense[] }>(
        `/time/cases/${individualId}/expenses`
      )
    ).then(expenseList),
  create: (individualId: string, body: NewExpense) =>
    call(() =>
      axiosInstance.post<{ expense: TimeExpense }>(
        `/time/cases/${individualId}/expenses`,
        body
      )
    ).then((d) => d.expense),
  update: (id: string, patch: Partial<NewExpense>) =>
    call(() =>
      axiosInstance.patch<{ expense: TimeExpense }>(
        `/time/expenses/${id}`,
        patch
      )
    ).then((d) => d.expense),
  confirm: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ expenses: TimeExpense[] }>(
        "/time/expenses/confirm",
        { ids }
      )
    ).then(expenseList),
  discard: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ expenses: TimeExpense[] }>(
        "/time/expenses/discard",
        { ids }
      )
    ).then(expenseList),
  restore: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ expenses: TimeExpense[] }>(
        "/time/expenses/restore",
        { ids }
      )
    ).then(expenseList),
  review: (filters: ExpenseFilters) =>
    call(() =>
      axiosInstance.get<{ expenses: TimeExpense[] }>("/time/review/expenses", {
        params: {
          ...filters,
          status: filters.status?.length ? filters.status.join(",") : undefined
        }
      })
    ).then(expenseList),
  approve: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ expenses: TimeExpense[] }>(
        "/time/review/expenses/approve",
        { ids }
      )
    ).then(expenseList),
  unapprove: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ expenses: TimeExpense[] }>(
        "/time/review/expenses/unapprove",
        { ids }
      )
    ).then(expenseList)
};

export const TimeAdminAPI = {
  settings: () =>
    call(() => axiosInstance.get<FirmTimeSettings>("/time/settings")),
  saveSettings: (patch: FirmTimeSettingsPatch) =>
    call(() => axiosInstance.put<FirmTimeSettings>("/time/settings", patch)),
  timekeepers: () =>
    call(() =>
      axiosInstance.get<{ timekeepers: Timekeeper[]; default_rate: number }>(
        "/time/timekeepers"
      )
    ),
  saveTimekeeper: (userId: string, patch: Partial<Timekeeper>) =>
    call(() =>
      axiosInstance.put<{ timekeeper: Timekeeper }>(
        `/time/timekeepers/${userId}`,
        patch
      )
    ).then((d) => d.timekeeper),

  entries: (filters: ReviewFilters) =>
    call(() =>
      axiosInstance.get<{
        entries: TimeEntry[];
        people: Record<string, string>;
      }>("/time/review/entries", {
        params: {
          ...filters,
          status: filters.status?.length ? filters.status.join(",") : undefined
        }
      })
    ),
  kpis: () => call(() => axiosInstance.get<ReviewKpis>("/time/review/kpis")),
  approve: (ids: string[], force = false) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/review/approve", {
        ids,
        force
      })
    ).then(entries),
  unapprove: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/review/unapprove", {
        ids
      })
    ).then(entries),
  returnEntries: (ids: string[], note: string) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/review/return", {
        ids,
        note
      })
    ).then(entries),
  unreturn: (ids: string[]) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/review/unreturn", {
        ids
      })
    ).then(entries),
  bulkEdit: (ids: string[], fields: Record<string, unknown>, note?: string) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>("/time/review/bulk", {
        ids,
        fields,
        note
      })
    ).then(entries),
  matters: () =>
    call(() =>
      axiosInstance.get<{ matters: MatterRow[] }>("/time/review/matters")
    ).then((d) => d.matters),
  waiting: (from?: string, to?: string) =>
    call(() =>
      axiosInstance.get<{ waiting: WaitingRow[] }>("/time/review/waiting", {
        params: { from, to }
      })
    ).then((d) => d.waiting),
  nudge: (userId: string, from?: string, to?: string) =>
    call(() =>
      axiosInstance.post<{ nudge: TimeNudge }>("/time/review/nudge", {
        user_id: userId,
        from,
        to
      })
    ).then((d) => d.nudge),

  providers: () =>
    call(() =>
      axiosInstance.get<{ providers: Provider[] }>("/time/providers")
    ).then((d) => d.providers),
  exports: () =>
    call(() =>
      axiosInstance.get<{ batches: ExportBatch[] }>("/time/exports")
    ).then((d) => d.batches),
  /** The export check's fix for lines above a company's approved rate. */
  priceAtApprovedRate: (entryIds: string[]) =>
    call(() =>
      axiosInstance.post<{ changed: string[] }>(
        "/time/exports/price-at-approved-rate",
        { entry_ids: entryIds }
      )
    ).then((d) => d.changed),
  previewExport: (req: ExportRequest) =>
    call(() => axiosInstance.post<ExportPreview>("/time/exports/preview", req)),
  createExport: (req: ExportRequest) =>
    call(() =>
      axiosInstance.post<{ batch: ExportBatch }>("/time/exports", req)
    ).then((d) => d.batch),
  voidExport: (batchId: string) =>
    call(() =>
      axiosInstance.post<{ batch: ExportBatch }>(
        `/time/exports/${batchId}/void`
      )
    ).then((d) => d.batch),
  downloadExport: (batchId: string) =>
    call(() =>
      axiosInstance.get<Blob>(`/time/exports/${batchId}/download`, {
        responseType: "blob"
      })
    )
};

/** Client guidelines and billing rules (firm admins). */
/** Pre-bill (firm admins): flags on released time, their fixes, matter approval. */
export const PrebillAPI = {
  matters: (range: { from?: string; to?: string }, user?: string) =>
    call(() =>
      axiosInstance.get<{ matters: PrebillMatterRow[] }>("/time/prebill", {
        params: { ...range, user }
      })
    ).then((d) => d.matters),
  matter: (individualId: string, user?: string) =>
    call(() =>
      axiosInstance.get<PrebillMatter>(
        `/time/prebill/matters/${encodeURIComponent(individualId)}`,
        { params: { user } }
      )
    ),
  evaluate: (individualId: string) =>
    call(() =>
      axiosInstance.post<PrebillMatter>(
        `/time/prebill/matters/${encodeURIComponent(individualId)}/evaluate`
      )
    ),
  approve: (individualId: string, entryIds?: string[]) =>
    call(() =>
      axiosInstance.post<{ entries: TimeEntry[] }>(
        `/time/prebill/matters/${encodeURIComponent(individualId)}/approve`,
        entryIds ? { entry_ids: entryIds } : {}
      )
    ).then(entries),
  flag: (
    flagId: string,
    action: FlagAction,
    body: { fix?: FlagFix; reason?: string; note?: string } = {}
  ) =>
    call(() =>
      axiosInstance.post<{ entry: TimeEntry }>(
        `/time/prebill/flags/${encodeURIComponent(flagId)}/${action}`,
        body
      )
    ).then((d) => d.entry)
};

export const GuidelineAPI = {
  list: (owner: GuidelineOwner, kind?: GuidelineKind) =>
    call(() =>
      axiosInstance.get<{ guidelines: TimeGuideline[] }>("/time/guidelines", {
        params: { ...owner, kind }
      })
    ).then((d) => d.guidelines),
  upload: (
    owner: GuidelineOwner,
    file: File,
    kind: GuidelineKind = "guidelines"
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    if (owner.company_id) form.append("company_id", owner.company_id);
    if (owner.foreign_national_id)
      form.append("foreign_national_id", owner.foreign_national_id);
    return call(() =>
      axiosInstance.post<TimeGuideline>("/time/guidelines", form, {
        headers: { "Content-Type": "multipart/form-data" }
      })
    );
  },
  remove: (id: string) =>
    call(() =>
      axiosInstance.delete<{ deleted: string }>(`/time/guidelines/${id}`)
    ),
  saveRules: (id: string, rules: Array<Partial<BillingRule>>) =>
    call(() =>
      axiosInstance.put<TimeGuideline>(`/time/guidelines/${id}/rules`, {
        rules
      })
    ),
  extract: (id: string) =>
    call(() =>
      axiosInstance.post<TimeGuideline>(`/time/guidelines/${id}/extract`)
    ),
  file: (id: string) =>
    call(() =>
      axiosInstance.get<Blob>(`/time/guidelines/${id}/file`, {
        responseType: "blob"
      })
    ),
  parseRule: (text: string) =>
    call(() => axiosInstance.post<RuleDraft>("/time/rules/parse", { text })),
  caseRules: (individualId: string) =>
    call(() =>
      axiosInstance.get<CaseRules>(`/time/cases/${individualId}/rules`)
    ),
  setCaseRule: (individualId: string, ruleId: string, on: boolean) =>
    call(() =>
      axiosInstance.put<CaseRules>(`/time/cases/${individualId}/rules`, {
        rule_id: ruleId,
        on
      })
    )
};

const apiBase = (): string =>
  String(import.meta.env.REACT_APP_API_BASE_URL ?? "").replace(/\/+$/, "");

export const TimeBeatsAPI = {
  async send(segments: BeatSegment[]): Promise<BeatsResponse> {
    try {
      const { data } = await axiosInstance.post<BeatsResponse>("/time/beats", {
        segments
      });
      return data;
    } catch (error) {
      return rethrow(error);
    }
  },

  /**
   * For `pagehide`: axios can't outlive the page, `fetch` with `keepalive`
   * can, and unlike `sendBeacon` it carries the auth header. The token is
   * read from the cache, so call `primeToken` while the page is alive.
   */
  sendOnUnload(segments: BeatSegment[], token: string | null): void {
    if (!segments.length || !token) return;
    try {
      void fetch(`${apiBase()}/time/beats`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ segments })
      }).catch(() => {});
    } catch {
      // The page is going away; nothing to recover.
    }
  },

  primeToken: getAuthToken
};
